import { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase/config";
import { useCart } from "../context/CartContext";
import { useNavigate } from "react-router-dom";
import { Package, ShoppingCart, Heart, Eye, Plus } from "lucide-react";

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImage, setCurrentImage] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [hoveredProduct, setHoveredProduct] = useState(null);
  
  const { addToCart } = useCart();
  const navigate = useNavigate();

  const categories = [
    { id: "All", icon: Package },
    { id: "Electronics", emoji: "⚡" },
    { id: "Clothing", emoji: "👕" },
    { id: "Furniture", emoji: "🪑" }
  ];

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "products"));
        const productsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setProducts(productsData);
      } catch (error) {
        console.error("Error fetching products:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const filteredProducts = products.filter((product) => 
    selectedCategory === "All" || product.category === selectedCategory
  );

  const handleAddToCart = (product, event) => {
    event.stopPropagation();
    addToCart(product);
    
    const toast = document.createElement('div');
    toast.className = 'fixed bottom-4 right-4 bg-black text-white px-6 py-3 rounded-xl shadow-2xl z-50 animate-slideIn';
    toast.innerHTML = `
      <div class="flex items-center space-x-2">
        <span>✨</span>
        <span>${product.name} added to cart</span>
      </div>
    `;
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.classList.add('animate-slideOut');
      setTimeout(() => toast.remove(), 300);
    }, 2000);
  };

  const handleBuyNow = (product, event) => {
    event.stopPropagation();
    addToCart(product);
    navigate("/checkout");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center space-y-4">
          <div className="relative w-16 h-16">
            <div className="absolute top-0 left-0 w-full h-full border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <div className="absolute top-2 left-2 w-12 h-12 border-4 border-blue-300 border-t-transparent rounded-full animate-spin"></div>
          </div>
          <p className="text-lg font-medium text-gray-600">Loading amazing products...</p>
        </div>
      </div>
    );
  }

  const openImageModal = (image) => {
    setCurrentImage(image);
    document.body.style.overflow = 'hidden';
  };

  const closeImageModal = () => {
    setCurrentImage(null);
    document.body.style.overflow = 'auto';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Hero Section */}
      <div className="bg-black text-white py-16 px-4">
        <div className="container mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Discover Our Collection</h1>
          <p className="text-gray-400 text-lg max-w-2xl">
            Explore our curated selection of premium products designed to elevate your lifestyle.
          </p>
        </div>
      </div>

      {/* Categories */}
      <div className="sticky top-0 bg-white/80 backdrop-blur-lg shadow-sm z-20">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-4 overflow-x-auto">
            <div className="flex space-x-2">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`
                    px-6 py-2 rounded-full text-sm font-medium transition-all
                    ${selectedCategory === cat.id 
                      ? 'bg-black text-white shadow-lg transform scale-105' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }
                  `}
                >
                  {cat.emoji ? (
                    <span className="mr-2">{cat.emoji}</span>
                  ) : (
                    <cat.icon className="w-4 h-4 inline-block mr-2" />
                  )}
                  {cat.id}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        {error && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-8 rounded-lg">
            <p className="font-medium">Error</p>
            <p>{error}</p>
          </div>
        )}

        {filteredProducts.length === 0 ? (
          <div className="text-center py-32">
            <div className="text-7xl mb-6">🔍</div>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">No products found</h2>
            <p className="text-gray-600 text-lg">Try selecting a different category</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500"
                onMouseEnter={() => setHoveredProduct(product.id)}
                onMouseLeave={() => setHoveredProduct(null)}
              >
                <div 
                  className="relative aspect-[4/5] overflow-hidden rounded-t-2xl"
                  onClick={() => openImageModal(product.image)}
                >
                  <img
                    src={product.image || "/api/placeholder/400/500"}
                    alt={product.name}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-10 transition-opacity"></div>
                  
                  {/* Quick Action Buttons */}
                  <div className="absolute top-4 right-4 flex flex-col space-y-2 transform translate-x-12 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-300">
                    <button 
                      className="p-2 bg-white rounded-full shadow-lg hover:scale-110 transition-transform"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Add wishlist functionality
                      }}
                    >
                      <Heart className="w-5 h-5" />
                    </button>
                    <button 
                      className="p-2 bg-white rounded-full shadow-lg hover:scale-110 transition-transform"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddToCart(product, e);
                      }}
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Category Tag */}
                  <div className="absolute bottom-4 left-4">
                    <span className="bg-black/75 backdrop-blur-md text-white px-4 py-1.5 rounded-full text-sm font-medium">
                      {product.category}
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <h2 className="text-xl font-bold text-gray-800 mb-2 line-clamp-1">
                    {product.name}
                  </h2>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {product.description}
                  </p>

                  <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Price</span>
                      <span className="text-2xl font-bold">${product.price.toFixed(2)}</span>
                    </div>
                    <button
                      onClick={(e) => handleBuyNow(product, e)}
                      className="px-6 py-2.5 bg-black text-white rounded-xl hover:bg-gray-800 transition-colors text-sm font-medium"
                    >
                      Buy Now
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Image Modal */}
      {currentImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm"
          onClick={closeImageModal}
        >
          <div 
            className="relative max-w-5xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeImageModal}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors flex items-center space-x-2"
            >
              <span>Close</span>
              <span className="text-2xl">×</span>
            </button>
            <img
              src={currentImage || "/api/placeholder/800/1000"}
              alt="Product"
              className="w-full h-auto max-h-[80vh] object-contain rounded-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;